import React from "react";
import { mdiArrowAll, mdiCheck, mdiClipboardEditOutline, mdiExclamation, mdiHistory, mdiRefresh } from "@mdi/js";
import Icon from "@mdi/react";
import { TextareaAutosize } from "@mui/material";
import { useTranslation } from "react-i18next";
import { defaultTender, useViewEditTenderStore } from "../../../../../../Stores/viewEditTender";
import { useVademecumStore } from "../../../../../../Stores/vademecumStore";
import { getQueryHistory, regenerateField, returnStatus, saveField } from "../Utility/utility";
import { useAuth } from "../../../../../../Services/auth";
import { QueryHistoryItem } from "./queryHistoryItem";
import dayjs from "dayjs";
import {
  normalizeListForComparison,
  interpretNoticeTypeHistoryValue,
  normalizeNoticeTypeFieldValue,
} from "../../../../../../Utils/listComparison";

export default function FieldValueListItemAdmin({ field, canViewHistory = true }) {
  const auth = useAuth();

  const { t, i18n } = useTranslation();

  const [summary, summaryIDToGET, summaryFieldValuesStatus] = useViewEditTenderStore((state) => [
    state.summary,
    state.summaryIDToGET,
    state.summaryFieldValuesStatus,
  ]);

  const [individualFieldStatus, individualFieldError, regenStatus, showQueryHistoryItems, setShowQueryHistoryItems] = useVademecumStore(
    (state) => [
      state.individualFieldStatus?.[`${field.id}`],
      state.individualFieldErrors?.[`${field.id}`],
      state?.regenStatus?.[`${field.id}`]?.status,
      state.showQueryHistoryItems,
      state.setShowQueryHistoryItems,
    ]
  );
  const fieldHistory = showQueryHistoryItems?.[field?.vademecum]?.[field?.field?.ai_checked_field_id];
  const canFetchHistory = Boolean(canViewHistory && field?.field?.ai_checked_field_id);
  const historyItems = React.useMemo(() => {
    if (!Array.isArray(fieldHistory?.items)) {
      return [];
    }
    return [...fieldHistory.items].sort(
      (a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()
    );
  }, [fieldHistory]);
  const hasHistoryItems = historyItems.length > 0;

  const normalizeValue = React.useCallback((value, options = {}) => {
    const { interpretAsList = false } = options;

    const normalizeScalar = (input) => {
      if (input === null || input === undefined) return "";
      if (typeof input === "string") {
        return input.replace(/\s+/g, " ").trim().toLowerCase();
      }
      if (Array.isArray(input)) {
        return input
          .map((entry) => normalizeScalar(entry))
          .join(" ")
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase();
      }
      if (typeof input === "object") {
        if (input?.name) {
          return normalizeScalar(input.name);
        }
        try {
          return normalizeScalar(JSON.stringify(input));
        } catch (err) {
          return "";
        }
      }
      return String(input).replace(/\s+/g, " ").trim().toLowerCase();
    };

    if (interpretAsList) {
      return normalizeListForComparison(value);
    }

    return normalizeScalar(value);
  }, []);

  const latestHistoryResult = hasHistoryItems ? historyItems[0]?.result ?? "" : "";
  const interpretAsList = field?.field?.name === "cpv";
  const interpretNoticeType = field?.field?.name === "notice_type";
  const normalizedFieldValue = interpretNoticeType
    ? normalizeNoticeTypeFieldValue(field?.value ?? "")
    : normalizeValue(field?.value ?? "", { interpretAsList });
  const normalizedHistoryValue = interpretNoticeType
    ? interpretNoticeTypeHistoryValue(latestHistoryResult)
    : normalizeValue(latestHistoryResult, { interpretAsList });

  const hasManualOverride =
    hasHistoryItems &&
    (normalizedHistoryValue === null ||
      normalizedFieldValue === null ||
      normalizedFieldValue !== normalizedHistoryValue);

  const manualIndicator = React.useMemo(() => {
    if (!canViewHistory) {
      return { show: false };
    }

    if (!canFetchHistory) {
      return {
        show: true,
        className: "tc-manual-icon-neutral",
        title: t("No AI generation logs for this field"),
      };
    }

    if (hasHistoryItems) {
      if (hasManualOverride) {
        return {
          show: true,
          className: "tc-manual-icon-edited",
          title: t("This field was manually updated after an AI generation"),
        };
      }
      return { show: false };
    }

    if (field?.source !== "tenderchain") {
      return {
        show: true,
        className: "tc-manual-icon-neutral",
        title: t("This field was manually provided and has no AI generation logs."),
      };
    }

    return { show: false };
  }, [canFetchHistory, field?.source, hasHistoryItems, hasManualOverride, t]);

  return (
    <div key={JSON.stringify(regenStatus)} className="d-flex align-items-center tc-flex-gap-10px">
      <Icon
        path={mdiArrowAll}
        className="tc-move-summary-field-arrows tc_grow_on_hover_icon cursor-pointer"
        title={t("Move field up or down")}
        size={0.8}
      />
      <div className="d-flex flex-column mb-2 w-full pe-2">
        <div className="d-flex tc-flex-gap-5px align-items-start">
          <div className="fw-bold">
            {`${
              field.field.translations[i18n.language.slice(0, 2)]
                ? field.field.translations[i18n.language.slice(0, 2)].display_name
                : field.field.translations[Object.keys(field.field.translations)[0]].display_name
            } `}
          </div>
          <div className="ms-auto d-flex align-items-center tc-flex-gap-5px">
            {canFetchHistory ? (
              <Icon
                path={mdiHistory}
                className="tc_grow_on_hover_icon cursor-pointer tc-20px-icon-size"
                title={t("View Query History Items")}
                size={1}
                onClick={() => {
                  setShowQueryHistoryItems(field?.vademecum, field?.field?.ai_checked_field_id);
                  getQueryHistory(auth, summary.tender, field?.field?.ai_checked_field_id, summaryIDToGET);
                }}
              />
            ) : null}
            {manualIndicator.show ? (
              <Icon
                path={mdiClipboardEditOutline}
                className={`tc-20px-icon-size ${manualIndicator.className ?? ""}`}
                title={manualIndicator.title}
                size={1}
              />
            ) : null}
            <div key={field.state} className="tc-text-muted">{`(${returnStatus(regenStatus)})`}</div>
            {field.error ? <div className="tc-formerror-colour-only">{` - ${field.error}`}</div> : null}
          </div>
        </div>

        {canViewHistory && fieldHistory?.show ? (
          fieldHistory?.status === "loading" ? (
            <div className="tc-spinner-small"></div> //TODO: change data structure to include the items below
          ) : (
            <div className="d-flex flex-column tc-cpv-list-max-height-322px mt-2 mb-2 border border-2 border-primary rounded p-3 tc-flex-gap-10px">
              {fieldHistory?.status === "error" ? (
                <div className="tc-formerror-colour-only">{t("There was an error getting the query history")}</div>
              ) : historyItems.length ? (
                historyItems.map((item) => <QueryHistoryItem key={item.id} item={item} />)
              ) : (
                <div className="tc-text-muted">{t("This field was manually provided and has no AI generation logs.")}</div>
              )}
            </div>
          )
        ) : null}

        <div className="d-flex align-items-start w-full tc-flex-gap-5px position-relative">
          <div
            className={`position-absolute tc-summary-field-loading ${
              individualFieldStatus === "saving" ? "visibleFade" : "hiddenFade"
            }`}
          >
            {t("Saving...")} <div className="tc-spinner-small "></div>
          </div>
          <div className="d-flex flex-column w-full">
            <TextareaAutosize
              key={`${field.value}_${regenStatus}`}
              className={`form-control h-full ${
                individualFieldStatus !== "saving" && individualFieldStatus !== "saved" && field.state === "COMPLETED"
                  ? "tc-text-area-green-outline"
                  : ""
              } ${
                summaryFieldValuesStatus === "saving" || Object.keys(defaultTender).includes(field.field.name)
                  ? "disabled"
                  : ""
              } ${
                individualFieldStatus === "saving"
                  ? "tc-text-area-yellow-outline"
                  : individualFieldStatus && individualFieldStatus === "saved"
                  ? "tc-text-area-green-outline"
                  : ""
              }`}
              onBlur={(e) => {
                saveField(
                  summaryIDToGET,
                  {
                    id: field.id,
                    value: e.target.value,
                    field: field.field.name,
                  },
                  auth
                );
              }}
              defaultValue={field.value}
              type="textarea"
            />
            {individualFieldError ? <div className="tc-formerror-colour-only mt-1">{individualFieldError}</div> : null}
          </div>

          <Icon
            path={regenStatus === "error" ? mdiExclamation : regenStatus === "success" ? mdiCheck : mdiRefresh}
            size={1.2}
            className={`${regenStatus === "IN_PROGRESS" ? "rotate tc-text-primary" : ""} tc-text-muted cursor-pointer`}
            onClick={() => {
              regenerateField(field.id, auth);
            }}
          />
        </div>
      </div>
    </div>
  );
}
